import { Image } from '@cue/atoms';
import { useResponsive } from '@cue/hooks';
import { ExternalContent } from '@cue/molecules';
import { Modal, useModal } from '@cue/organisms';
import styled from '@emotion/styled';
import { parseGatsbyImage } from '@project/cms/pre-hooks';
import React, { useState } from 'react';
import { CMS_Content_Module_IFrame } from './types';

type IframeContentModuleProps = CMS_Content_Module_IFrame['data'];
type MappedUrls = {
  [key: string]: string;
};

export const IframeContentModule: React.FC<IframeContentModuleProps> = (props) => {
  const { url: _url, ratio, open_in_modal, teaser_image, wildcard_mapping } = props;

  const [hovering, setHovering] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalRef] = useModal();
  const btnRef = React.useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive();
  const [switchCursor, setSwitchCursor] = React.useState<boolean>(false);
  const [url, setUrl] = useState(_url);
  React.useEffect(() => {
    function dragBtn(e: MouseEvent) {
      e.stopPropagation();

      const btn = btnRef.current;
      if (!btn || !switchCursor) return;
      // if (windowWidth < 1080) {
      //   btn.style.left = '50%';
      //   btn.style.top = '50%';
      //   return;
      // }
      const mousePos = { x: e.pageX, y: e.pageY };

      btn.style.left = mousePos.x + 'px';
      btn.style.top = mousePos.y + 'px';
    }
    addEventListener('mousemove', dragBtn);
    return () => removeEventListener('mousemove', dragBtn);
  }, []);

  React.useEffect(() => {
    if ('ontouchstart' in document.documentElement) {
      return;
    }
    setSwitchCursor(true);
  }, []);

  const parseWildcards = () => {
    const mapped: MappedUrls | undefined = wildcard_mapping
      ?.split('\n')
      .reduce((acc: MappedUrls, line: string) => {
        const parts: string[] = line.split(';');
        if (parts.length === 2) {
          const [key, value]: string[] = parts;
          acc[key] = value;
        } else {
          console.error(`Invalid line: ${line}`);
        }
        return acc;
      }, {});

    return mapped;
  };

  React.useEffect(() => {
    const wildcards = parseWildcards();
    const wildcard = window.location.pathname.split('/').filter(Boolean).pop();
    if (wildcards && wildcard && wildcards[wildcard]) {
      setUrl(wildcards[wildcard]);
      if (open_in_modal) {
        modalRef.current?.open();
      }
    }
  }, [modalRef]);

  function closeModal() {
    setIsModalOpen(false);
  }

  function openModal() {
    setIsModalOpen(true);
  }

  if (open_in_modal) {
    const teaserImage = teaser_image ? parseGatsbyImage(teaser_image) : null;

    return (
      <>
        {teaserImage?.src && (
          <div>
            <div
              className={`iframe-preview-container${hovering ? ' open-cursor' : ''}`}
              onMouseEnter={switchCursor ? () => setHovering(true) : undefined}
              onMouseLeave={switchCursor ? () => setHovering(false) : undefined}
              onClick={() => openModal()}
              style={{ position: 'relative' }}>
              <Image src={teaserImage.src} alt={'Boothmap Preview'} />
              <OpenButtonMobile hovering={!switchCursor} ref={btnRef}>
                <Image src={'/assets/images/open-cursor-2x.png'} alt={'Open Modal'} />
              </OpenButtonMobile>
            </div>
            {switchCursor ? (
              <OpenButton hovering={!isModalOpen && hovering} ref={btnRef}>
                <Image src={'/assets/images/open-cursor-2x.png'} alt={'Open Modal'} />
              </OpenButton>
            ) : null}

            <Modal
              show={isModalOpen}
              onVisibilityChange={(isOpen) => setIsModalOpen(isOpen)}
              ref={modalRef}
              onClose={() => {
                closeModal();
              }}
              className="iframe-modal"
              overrideCSS={{
                '.cue-modal-content': {
                  position: 'relative',
                  width: '100%',
                  height: isMobile ? '100%' : 'calc(100% - 30px)',
                  maxWidth: '100%!important',
                  padding: 0,
                },

                '.cue-modal-backdrop': {
                  padding: 0,
                  '.cue-modal-container': {
                    maxWidth: '100%',
                    width: isMobile ? '100%' : '95%',
                    height: isMobile ? '100%' : 'calc(100% - 30px)',
                    ...(isMobile ? { padding: 0 } : {}),
                  },
                },
              }}>
              <ExternalContent uri={url} ratio={ratio} />
            </Modal>
          </div>
        )}
      </>
    );
  }

  return <ExternalContent uri={url} ratio={ratio} />;
};

const OpenButton = styled.div<{ hovering: boolean }>(({ hovering }) => ({
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
  cursor: 'pointer',
  width: 80,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, 0%)',

  opacity: hovering ? 1 : 0,
}));

const OpenButtonMobile = styled.div<{ hovering: boolean }>(({ hovering }) => ({
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
  cursor: 'pointer',
  width: 80,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, 0%)',

  opacity: hovering ? 1 : 0,
}));
